import * as THREE from 'three';
import type { CameraIntrinsics } from './multisetRestClient';

/**
 * Raw camera access via getUserMedia — works in stock iOS Safari (unlike
 * WebXR). This is the "Zappar/8th Wall-style" bypass: we never touch
 * navigator.xr, just a <video> element fed by the environment-facing camera.
 *
 * ⚠️ INTRINSICS CAVEAT — read before trusting localization confidence:
 * MultiSet's REST API needs real focal length / principal point
 * (cameraIntrinsics: fx, fy, px, py) for accurate pose estimation. WebXR and
 * native ARKit/ARCore hand these to the SDK automatically from the device's
 * calibrated camera. Plain getUserMedia on iOS Safari exposes NO such API —
 * there is no standard way to read a device's real focal length from the
 * browser. `estimateIntrinsics` below approximates fx/fy from resolution and
 * an assumed horizontal field of view (typical iPhone main/wide camera is
 * roughly 60-70°; we use 65° as a middle-of-the-road default). This is a
 * genuine accuracy tradeoff vs. the native app / Android WebXR path — expect
 * lower confidence scores and possibly more failed localizations,
 * especially on devices whose real FOV differs a lot from the assumption.
 * Companies like Zappar/8th Wall maintain internal per-device-model
 * calibration tables to do better here; that's out of scope for v1. If
 * accuracy is too low in practice, the next step is building a small
 * device-model -> FOV lookup table (navigator.userAgent sniffing) rather
 * than a single global constant.
 */

// Exported (not just a local const) so the Phase 0 spike's three.js camera
// (app-web-engine/spike/) can match the exact same FOV assumption used to
// build the intrinsics sent to MultiSet — same reasoning either place uses
// it, single source of truth.
export const ASSUMED_HORIZONTAL_FOV_DEG = 65;

export async function startCamera(video: HTMLVideoElement): Promise<MediaStream> {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: {
      facingMode: { ideal: 'environment' },
      width: { ideal: 1280 },
      height: { ideal: 960 },
    },
    audio: false,
  });
  video.srcObject = stream;
  video.setAttribute('playsinline', 'true'); // required to avoid iOS Safari full-screen takeover
  video.muted = true;
  await video.play();
  return stream;
}

export function stopCamera(stream: MediaStream | null): void {
  stream?.getTracks().forEach((t) => t.stop());
}

/** Captures the current video frame as a base64 JPEG data URL, downscaled to MultiSet's 1280px max. */
export function captureFrame(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
): { dataUrl: string; width: number; height: number } {
  const maxDim = 1280;
  const scale = Math.min(1, maxDim / Math.max(video.videoWidth, video.videoHeight));
  const width = Math.round(video.videoWidth * scale);
  const height = Math.round(video.videoHeight * scale);

  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(video, 0, 0, width, height);

  const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
  return { dataUrl, width, height };
}

/** See the intrinsics caveat above — this is an approximation, not a measurement. */
export function estimateIntrinsics(width: number, height: number): CameraIntrinsics {
  const fovRad = (ASSUMED_HORIZONTAL_FOV_DEG * Math.PI) / 180;
  const fx = width / (2 * Math.tan(fovRad / 2));
  // Assume square pixels (fy == fx scaled by aspect is unnecessary since fx
  // already derives from horizontal FOV/width; using the same focal length
  // for both axes is the standard approximation when only one FOV is known).
  const fy = fx;
  return { fx, fy, px: width / 2, py: height / 2 };
}

/**
 * Real (not guessed) intrinsics, derived from 8th Wall's live-tracked
 * camera (eightWallSession.ts) instead of the fixed-FOV approximation
 * above. 8th Wall's `XR8.Threejs.xrScene().camera` is a THREE.PerspectiveCamera
 * that 8th Wall keeps synced to the ACTUAL device camera's field of view
 * every frame — its own AR pass-through rendering only visually aligns with
 * the real world if this is accurate, so `camera.fov` (vertical FOV,
 * degrees — a standard THREE.PerspectiveCamera property) is real
 * device-measured data, not a constant. Use this whenever a live-tracked
 * camera is available (currently only the 8th Wall path); estimateIntrinsics()
 * above remains the fallback for the plain-getUserMedia secondary path
 * (cameraArSession.ts), which has no such tracked camera to read from.
 */
export function intrinsicsFromTrackedCamera(
  camera: THREE.PerspectiveCamera,
  width: number,
  height: number,
): CameraIntrinsics {
  const verticalFovRad = (camera.fov * Math.PI) / 180;
  const fy = height / (2 * Math.tan(verticalFovRad / 2));
  const fx = fy; // square-pixel assumption — same reasoning as estimateIntrinsics() above
  return { fx, fy, px: width / 2, py: height / 2 };
}

export function isCameraCaptureSupported(): boolean {
  return Boolean(navigator.mediaDevices?.getUserMedia);
}
